import type { CommentReaction } from '@liveblocks/client'

import { cn } from '@/lib/ui/style-helpers'

import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

import { COMMENT_LABELS, otherReactorsLabel } from './labels'
import { UserName } from './user-name'
import { splitReactors } from './utils'

type CommentReactionsProps = {
  reactions: readonly CommentReaction[]
  currentUserId?: string
  readOnly?: boolean
  onToggle: (emoji: string, reacted: boolean) => void
}

export const CommentReactions = ({
  reactions,
  currentUserId,
  readOnly,
  onToggle,
}: CommentReactionsProps) => {
  if (!reactions.length) return null

  return (
    <div className='flex flex-wrap gap-1'>
      {reactions.map((reaction) => {
        const reacted = !!currentUserId && reaction.users.some((user) => user.id === currentUserId)
        const { named, othersCount } = splitReactors(reaction.users)

        return (
          <Tooltip
            key={reaction.emoji}
            asChild
            // The roll-up line needs the wider variant; a lone name fits the default.
            size={reaction.users.length > 1 ? 'large' : 'default'}
            trigger={
              <button
                type='button'
                disabled={readOnly}
                onClick={() => onToggle(reaction.emoji, reacted)}
                className={cn(
                  'flex h-6 items-center gap-1 rounded-full px-2 py-1 disabled:cursor-default',
                  reacted
                    ? 'bg-main-background-subtle-semantic-accent text-main-foreground-accent'
                    : 'bg-main-background-neutral text-main-foreground-secondary',
                )}
              >
                <span className='text-body-small'>{reaction.emoji}</span>
                <span className='text-label-small'>{reaction.users.length}</span>
              </button>
            }
          >
            <span>
              <span className='font-bold'>
                {named.map((user, index) => (
                  <span key={user.id}>
                    {index > 0 && ', '}
                    <UserName userId={user.id} />
                  </span>
                ))}
                {othersCount > 0 && `, ${otherReactorsLabel(othersCount)}`}
              </span>
              <span className='text-main-foreground-tertiary'>
                {` ${COMMENT_LABELS.reactedWith} ${reaction.emoji}`}
              </span>
            </span>
          </Tooltip>
        )
      })}
    </div>
  )
}
